import { Journey } from '@audibene/ta.utils-opportunity'
import { Button } from '@mui/material'
import { differenceInBusinessDays, differenceInCalendarDays } from 'date-fns'
import { useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { useNavigate, useParams } from 'react-router'
import { AppointmentType, Country, Route, EventType, RouteParameters, ToastSubType } from '../../../../../constants'
import {
  AppointmentRescheduleLocationState,
  useAppointmentLocationState,
  useAppointmentTypes,
  useEventTracking,
  useFeatureFlags,
  useFormAnalytics,
  useIsFitterSchedulingMode,
  useIsManualMode,
  useNotification,
  useNow,
  useScheduleFormDefaultValues,
  useTrackError,
} from '../../../../../hooks'
import { useAppointmentsService } from '../../../../../hooks/services'
import {
  Appointment,
  AppointmentCreateRequest,
  AppointmentFormData,
  AppointmentRescheduleRequest,
  RescheduleEventData,
} from '../../../../../types'
import { hydratePathname, isSlotUnavailableError, logFactory } from '../../../../../util'
import { SubmitButton } from '../../../../forms'
import { Schedule } from '../../../../schedule'
import { Spacer } from '../../../../Spacer'
import { Title } from '../../../../Title'
import { handleSubmissionFailure } from '../../../util'
import { Loading } from '../Loading'

const log = logFactory('RescheduleForm')
const saveErrorMessage = 'Sorry, there was an error while rescheduling this appointment'

const CallToActions = () => {
  const navigate = useNavigate()
  const { formState } = useFormContext()
  const { isSubmitting, isValidating } = formState
  const isLoading = isSubmitting || isValidating

  const onBack = () => navigate(-1)

  return (
    <>
      <Button color="primary" disabled={isLoading} onClick={onBack} variant="text">
        Back
      </Button>
      <SubmitButton />
    </>
  )
}

const Content = () => {
  const defaultValues = useScheduleFormDefaultValues()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { data: now = new Date() } = useNow()
  const { appointmentTypes } = useAppointmentTypes()
  const { notify } = useNotification()
  const { readSummary } = useFormAnalytics<AppointmentFormData>()
  const { track: trackEvent } = useEventTracking()
  const trackError = useTrackError()
  const { rescheduleAppointment } = useAppointmentsService()
  const { [RouteParameters.APPOINTMENT_ID]: appointmentId } = useParams()
  const state = useAppointmentLocationState() as AppointmentRescheduleLocationState
  const isFitterSchedulingMode = useIsFitterSchedulingMode()
  const { isManualMode } = useIsManualMode()
  const [isAvailabilityCacheEnabled] = useFeatureFlags<boolean>(['ENABLE_AVAILABILITY_CACHE'])

  const onSubmit = useCallback(
    async (data: AppointmentFormData): Promise<Appointment | undefined> => {
      log('📆 Transforming form data...', data)

      const type = data.type as AppointmentType
      const country = data.country as Country
      const journey = data.journey as Journey

      let created: Appointment | undefined

      const createRequest: AppointmentCreateRequest = {
        country,
        customer_salesforce_id: data?.customer_salesforce_id as string,
        duration_in_minutes: data?.duration_in_minutes as number,
        is_manual: isManualMode,
        journey,
        opportunity_gid: data?.opportunity_gid as string,
        resource_ids: data.resource_ids as string[],
        scheduled_customer_timezone: data.customer_timezone as string,
        start_date: data.start_date as Date,
        title: data.title as string,
        type,
      }

      const customerDefaultTimeZone = data?.customer_timezone as string
      const startDate = data?.start_date as Date
      const { didError, errorCounts, totalClicks, totalSessionDurationInSeconds, submitCount, validInputTimings } = readSummary()

      const eventData: RescheduleEventData = {
        appointmentId: appointmentId as string,
        customerDefaultTimeZone,
        customerTimeZone: data.customer_timezone as string,
        daysInTheFuture: differenceInCalendarDays(startDate, now),
        daysInTheFutureBusiness: differenceInBusinessDays(startDate, now),
        didError,
        durationDefaultInMinutes: appointmentTypes?.[type]?.duration_in_minutes,
        durationInMinutes: data?.duration_in_minutes as number,
        errorCountResourceNotSelected: errorCounts['resource_ids'] ?? 0,
        errorCountStartDayNotSelected: errorCounts['clientStartDate'] ?? 0,
        errorCountTimePickerInvalid: errorCounts['clientStartTime'] ?? 0,
        formType: 'reschedule',
        reason: state.reason,
        resourceTimeZone: data?.fitter_timezone as string,
        secondsToValidTime: validInputTimings['clientStartTime'],
        startDate: startDate.toISOString(),
        startDay: startDate.getDate(),
        startDayOfWeek: startDate.getDay(),
        startTimeHour: Number(
          startDate.toLocaleTimeString('en', { timeZone: data.customer_timezone as string, hour: 'numeric', hour12: false })
        ),
        startTimeMinute: Number(
          startDate.toLocaleTimeString('en', { timeZone: data.customer_timezone as string, minute: 'numeric', hour12: false })
        ),
        submissionCount: submitCount + 1, // We only increment submission count when validation fails, so we need to bump one for passing validation
        totalClicks,
        totalSessionDurationInSeconds,
        type: type as string,
      }

      try {
        const rescheduleRequest: AppointmentRescheduleRequest = {
          appointmentIdRescheduled: appointmentId as string,
          newAppointmentRequest: createRequest,
          description: state.description,
          reason: state.reason,
        }

        log('📩 Submitting reschedule request...', rescheduleRequest)

        created = await rescheduleAppointment(rescheduleRequest)

        trackEvent(EventType.APPOINTMENT_FORM_SUBMISSION_SUCCEEDED, { data: eventData })

        log(`📆 Successfully rescheduled appointment`, rescheduleRequest)

        const pathname = hydratePathname(Route.APPOINTMENT_RESCHEDULE_SUCCESS, {
          [RouteParameters.COUNTRY]: created.country,
          [RouteParameters.APPOINTMENT_ID]: created.id,
        })

        navigate(pathname, { replace: true })
      } catch (rescheduleError: any) {
        const failedEventData = {
          data: eventData,
          type: EventType.APPOINTMENT_FORM_SUBMISSION_FAILED,
        }

        const invalidateQueries = () => queryClient.invalidateQueries()

        handleSubmissionFailure({
          error: rescheduleError,
          eventData: failedEventData,
          fallbackErrorMessage: saveErrorMessage,
          invalidateQueries,
          notify,
          toastSubType: ToastSubType.RESCHEDULE_FAILURE,
          trackError,
          trackEvent,
        })

        if (isSlotUnavailableError(rescheduleError)) {
          trackEvent(EventType.APPOINTMENT_RESCHEDULE_FORM_SUBMISSION_FAILED_SLOT_UNAVAILABLE, {
            data: { ...eventData, wasAvailabilityCacheEnabled: isAvailabilityCacheEnabled },
          })
        }

        // Rethrow so our form's isSubmitSuccessful isn't set to true
        throw rescheduleError
      }

      return created
    },
    [appointmentId, appointmentTypes, isAvailabilityCacheEnabled, navigate, notify, now, readSummary, trackError, trackEvent]
  )

  return (
    <>
      {!isFitterSchedulingMode && (
        <>
          <Title>New Appointment</Title>
          <Spacer count={2} />
        </>
      )}
      {defaultValues ? (
        <Schedule defaultValues={defaultValues} isConfigurationReadOnly onSubmit={onSubmit}>
          <CallToActions />
        </Schedule>
      ) : (
        <Loading />
      )}
    </>
  )
}

export { Content }
